import React, {useEffect} from 'react'
import Text from '../../components/text/Text'
import WizButton from '../../components/button/WizButton'
import UnderLineInput from '../../components/input/UnderLineInput'
import {editVolumeLeague} from '../../apis/volume_league/edit'
import {IGroup} from './types'

interface IProps {
    group: IGroup
    globalMaxSort: number
    onSaved: () => Promise<any> | void
}

/**
 * 표시 순서(sort_value) 빠른 변경 패널 — 폼이 닫혀 있고 특정 그룹이 선택된 상태에서 노출.
 * 그룹 내 전체 레코드에 일괄 적용. 리스트가 길어 드래그가 번거로울 때 사용.
 * 입력/저장/완료 애니메이션 상태는 이 컴포넌트가 직접 소유한다.
 */
const QuickSortPanel: React.FC<IProps> = ({group, globalMaxSort, onSaved}) => {
    const [sortInput, setSortInput] = React.useState('')
    const [saving, setSaving] = React.useState(false)
    // 저장 완료 후 잠깐 보여줄 체크 애니메이션 상태 (끝나면 다시 disabled 저장으로 복귀)
    const [done, setDone] = React.useState(false)

    // 선택 그룹이 바뀌면(또는 저장 후 값이 갱신되면) 입력값을 현재 sortValue 로 동기화
    useEffect(() => {
        setSortInput(String(group.sortValue))
    }, [group.group, group.sortValue])

    const onSave = async () => {
        const val = Math.max(0, Number(sortInput) || 0)
        const ids = group.cards.reduce<number[]>((acc, c) => acc.concat(c.items.map(l => l.id)), [])
        setSaving(true)
        try {
            await Promise.all(ids.map(id => editVolumeLeague({id, sort_value: val})))
            await onSaved()
            setDone(true)
            setTimeout(() => setDone(false), 1400)
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="w-[1px] bg-line02" />
            <article className="w-full max-w-[560px]">
                <Text className="text-h2 text-font01">표시 순서 빠른 변경</Text>
                <Text className="text-body3 text-font02 mt-[6px]">
                    "{group.group}" 그룹 전체에 적용됩니다. 리스트가 길어 드래그가 번거로울 때 사용하세요.
                </Text>
                <div className="mt-4 p-6 border border-line02 bg-white rounded-[6px]">
                    <section>
                        <Text className="text-h3 text-font01">표시 순서 (Sort Value)</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            숫자가 클수록 목록 위에 노출됩니다. 현재 가장 높은 sort_value 는{' '}
                            <span className="font-bold text-sub_blue">{globalMaxSort}</span> 입니다.
                        </Text>
                        <div className="mt-[10px] flex items-end gap-x-[10px]">
                            <div className="w-[120px]">
                                <UnderLineInput
                                    inputClassName={'w-full text-body2'}
                                    type="number"
                                    value={sortInput}
                                    placeholder={'0'}
                                    onChanged={t => setSortInput(t)}
                                />
                            </div>
                            {done ? (
                                <div className="flex items-center gap-x-[6px] text-primary h-[40px]">
                                    <style>{`@keyframes vlCheckDraw{from{stroke-dashoffset:30}to{stroke-dashoffset:0}}@keyframes vlCheckPop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}.vl-check{animation:vlCheckPop .35s ease-out forwards}.vl-check-path{stroke-dasharray:30;stroke-dashoffset:30;animation:vlCheckDraw .4s .12s ease-out forwards}`}</style>
                                    <svg className="vl-check" viewBox="0 0 24 24" width="22" height="22">
                                        <path
                                            className="vl-check-path"
                                            d="M5 13l4 4L19 7"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <Text className="text-body2 font-bold text-primary">저장 완료</Text>
                                </div>
                            ) : (
                                <WizButton
                                    text={saving ? '저장 중…' : '저장'}
                                    type="primary"
                                    disabled={saving || Math.max(0, Number(sortInput) || 0) === group.sortValue}
                                    onClick={onSave}
                                />
                            )}
                        </div>
                    </section>
                </div>
            </article>
        </>
    )
}

export default QuickSortPanel
